import React, { useRef } from 'react';
import { styled } from '@mui/material';
import { CloudUpload, Close } from '@mui/icons-material';
import { Colors } from 'library';

interface ImageUploadProps {
    imageUrl: string | null;
    onFileSelected: (file: File) => void;
    onRemove: () => void;
    placeholder?: string;
    hint?: string;
    variant?: 'square' | 'wide';
    accept?: string;
}

export function ImageUpload({
    imageUrl,
    onFileSelected,
    onRemove,
    placeholder = 'Upload image',
    hint,
    variant = 'square',
    accept = 'image/*',
}: ImageUploadProps) {
    const inputRef = useRef<HTMLInputElement>(null);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) onFileSelected(file);
        e.target.value = '';
    };

    return (
        <>
            {imageUrl ? (
                <ThumbnailWrapper>
                    <Thumbnail
                        src={imageUrl}
                        alt=""
                        onClick={() => inputRef.current?.click()}
                        variant={variant}
                    />
                    <RemoveButton onClick={onRemove}>
                        <Close sx={{ fontSize: 14, color: Colors.white }} />
                    </RemoveButton>
                </ThumbnailWrapper>
            ) : (
                <UploadArea onClick={() => inputRef.current?.click()}>
                    <CloudUpload sx={{ fontSize: 20, color: Colors.mediumGrey }} />
                    <UploadText>{placeholder}</UploadText>
                </UploadArea>
            )}
            <input ref={inputRef} type="file" accept={accept} hidden onChange={handleChange} />
            {hint && <Hint>{hint}</Hint>}
        </>
    );
}

const ThumbnailWrapper = styled('div')({
    position: 'relative',
    display: 'inline-flex',
    alignSelf: 'flex-start',
});

const Thumbnail = styled('img')<{ variant: 'square' | 'wide' }>(({ variant }) => ({
    width: variant === 'square' ? 56 : '100%',
    height: variant === 'square' ? 56 : 80,
    objectFit: variant === 'square' ? 'contain' : 'cover',
    borderRadius: 8,
    border: `1px solid ${Colors.lightGrey}`,
    cursor: 'pointer',
}));

const RemoveButton = styled('button')({
    position: 'absolute',
    top: -8,
    right: -8,
    width: 20,
    height: 20,
    borderRadius: '50%',
    border: 'none',
    backgroundColor: Colors.mediumRed,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 0,
});

const UploadArea = styled('div')({
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '14px 16px',
    border: `1.5px dashed ${Colors.lightGrey}`,
    borderRadius: 10,
    cursor: 'pointer',
    '&:hover': { borderColor: Colors.mediumGrey },
});

const UploadText = styled('span')({
    fontSize: 14,
    color: Colors.mediumGrey,
});

const Hint = styled('span')({
    fontSize: 12,
    color: Colors.lightGrey,
});
